import { Card, CardContent, CardHeader, Typography, Stack } from "@altimateai/lego";
import DataTable from "@/components/tables/DataTable";
import type { TableHeaderType } from "@/components/tables/types";
import { RelativeComponentLoader } from "@/components/loader";
import { formatCost } from "@/helpers/utils";
import { useAppState } from "@/modules/app/useAppContext";
import { useWorkspacesCostBreakdown } from "../hooks";
import type { CostBreakdownItem } from "../types";

const EMPTY_STATE_CLASS = "al-text-gray-500 al-text-center al-py-8";

interface Props {
  startDate: string;
  endDate: string;
  workspaceId?: string;
}

const WorkspacesCostTable = ({ startDate, endDate, workspaceId }: Props) => {
  const { currency } = useAppState();

  const { data: workspaceBreakdownData, isLoading } = useWorkspacesCostBreakdown(
    {
      start_date: startDate,
      end_date: endDate,
      group_by: "workspace",
      workspace_id: workspaceId,
    },
    true
  );

  const filteredData =
    workspaceBreakdownData?.breakdown?.filter(
      item => item.name.toLowerCase() !== "unknown" && item.estimated_cost > 0
    ) || [];

  const header: TableHeaderType<CostBreakdownItem>[] = [
    {
      id: 1,
      uid: "name",
      label: "Workspace",
      render: item => <Typography size="sm">{item.name}</Typography>,
    },
    {
      id: 2,
      uid: "estimated_cost",
      label: "Total Cost",
      render: item => (
        <Typography size="sm">{formatCost(item.estimated_cost, currency)}</Typography>
      ),
    },
    {
      id: 3,
      uid: "total_dbus",
      label: "Total DBUs",
      render: item => (
        <Typography size="sm">
          {item.total_dbus.toLocaleString(undefined, { maximumFractionDigits: 0 })}
        </Typography>
      ),
    },
    {
      id: 4,
      uid: "pct_of_total",
      label: "% of Total",
      render: item => <Typography size="sm">{item.pct_of_total.toFixed(1)}%</Typography>,
    },
  ];

  return (
    <Card>
      <CardHeader>
        <Stack justify="between">
          <Typography variant="subheading" weight="medium">
            Workspaces Cost Details
          </Typography>
        </Stack>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <RelativeComponentLoader componentHeight={30} label="Loading workspaces cost details" />
        ) : filteredData.length === 0 ? (
          <Typography className={EMPTY_STATE_CLASS}>No workspace data available</Typography>
        ) : (
          <DataTable header={header} data={{ items: filteredData }} enableRowSelection={false} />
        )}
      </CardContent>
    </Card>
  );
};

export default WorkspacesCostTable;
